import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRealtimePostgresChanges } from './useRealtimePostgresChanges';
import type { Tables } from '@/integrations/supabase/types';
import { readPlanCache, writePlanCache } from '@/lib/planCache';

/**
 * Preisumbau: `addon_mc_booked` / `addon_language_booked` liegen live in der
 * DB (und kommen ueber das `select('*')` unten mit), sind aber noch nicht in
 * den generierten Typen (`src/integrations/supabase/types.ts` ist ein
 * Snapshot). Lokale Erweiterung statt Hand-Edit der generierten Datei — der
 * naechste `generate_typescript_types`-Lauf macht sie redundant.
 */
export type Subscription = Tables<'subscriptions'> & {
  addon_mc_booked?: number | null;
  addon_language_booked?: number | null;
};

export function useSubscription() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Seed the last-known subscription from localStorage for an instant render on
  // reload; revalidated in the background and via the realtime subscription below.
  const seed = useMemo(
    () => readPlanCache<Subscription>('subscription', user?.id),
    [user?.id],
  );

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();
      const row = (data as Subscription | null) ?? null;
      if (row) writePlanCache('subscription', user.id, row);
      return row;
    },
    enabled: !!user?.id,
    initialData: seed?.data,
    initialDataUpdatedAt: seed?.updatedAt,
  });

  // Realtime updates.
  //
  // Needs subscriptions to be a member of the publication supabase_realtime — it was
  // not until migration 20260726220000_realtime_missing_job_tables.sql, and the server
  // said so out loud on every join ("Unable to subscribe to changes with given
  // parameters"), which nothing was listening for. Until then a plan change made by the
  // Stripe webhook only became visible after a reload.
  //
  // Mounted by three components at once (Settings, GeneralTab, useCheckoutSuccess). On
  // the old shared topic all three landed on ONE channel with three bindings but a
  // single filter in the join payload, and the join-ok reconciliation tore that channel
  // down. useRealtimePostgresChanges gives each instance its own topic; the mechanism is
  // documented there.
  useRealtimePostgresChanges(
    user?.id ? 'subscription-changes' : null,
    user?.id
      ? { event: 'UPDATE', table: 'subscriptions', filter: `user_id=eq.${user.id}` }
      : null,
    () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
    },
  );

  return { subscription, isLoading };
}
